import { Box, Divider, Paper, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { fetchCep, formatCep, type ViaCepResult } from '@services/profile/viaCep.service';

import { CEP_REGEX, STATES, hasAnyContent, type ProfileFormValues } from './profileForm.helpers';

import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

export interface AddressSectionProps {
  register: UseFormRegister<ProfileFormValues>;
  errors: FieldErrors<ProfileFormValues>;
  setValue: UseFormSetValue<ProfileFormValues>;
  watch: UseFormWatch<ProfileFormValues>;
}

type AutofillField = 'state' | 'city' | 'district' | 'address';

const NO_LOCKS: Record<AutofillField, boolean> = {
  state: false,
  city: false,
  district: false,
  address: false,
};

const lockedSx = (locked: boolean) => (locked ? { backgroundColor: 'action.hover' } : undefined);

export const AddressSection = ({ register, errors, setValue, watch }: AddressSectionProps) => {
  const cep = watch('addressData.cep');
  const addressValues = watch('addressData');
  const [locked, setLocked] = useState<Record<AutofillField, boolean>>(NO_LOCKS);
  const [cepNotFound, setCepNotFound] = useState(false);

  const requiredWhenFilled = (message: string) => (value: string) => {
    if (hasAnyContent(addressValues) && !value?.trim()) return message;
    return true;
  };

  useEffect(() => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) {
      setLocked(NO_LOCKS);
      setCepNotFound(false);
      return undefined;
    }

    let active = true;
    fetchCep(digits).then((data) => {
      if (!active) return;
      if (!data) {
        setLocked(NO_LOCKS);
        setCepNotFound(true);
        return;
      }
      setCepNotFound(false);

      const next: Record<AutofillField, boolean> = { ...NO_LOCKS };
      const apply = (field: AutofillField, key: keyof ViaCepResult) => {
        const incoming = data[key];
        if (typeof incoming === 'string' && incoming.trim() !== '') {
          setValue(`addressData.${field}` as const, incoming, {
            shouldDirty: true,
            shouldValidate: true,
          });
          next[field] = true;
        }
      };
      apply('state', 'uf');
      apply('city', 'localidade');
      apply('district', 'bairro');
      apply('address', 'logradouro');
      setLocked(next);
    });

    return () => {
      active = false;
    };
  }, [cep, setValue]);

  const cepField = register('addressData.cep', {
    validate: (value) => {
      if (!value) return hasAnyContent(addressValues) ? 'CEP é obrigatório' : true;
      return CEP_REGEX.test(value) || 'CEP inválido (use 00000-000)';
    },
  });

  return (
    <Paper sx={{ p: 3 }} role="group" aria-labelledby="endereco-legend">
      <Typography id="endereco-legend" variant="h6" gutterBottom>
        Endereço
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <TextField
          label="CEP"
          placeholder="00000-000"
          inputProps={{ inputMode: 'numeric', maxLength: 9 }}
          {...cepField}
          onChange={(e) => {
            e.target.value = formatCep(e.target.value);
            cepField.onChange(e);
          }}
          error={!!errors.addressData?.cep || cepNotFound}
          helperText={errors.addressData?.cep?.message ?? (cepNotFound ? 'CEP não encontrado' : undefined)}
        />
        <TextField
          label="Estado (UF)"
          select
          SelectProps={{ native: true }}
          disabled={locked.state}
          sx={lockedSx(locked.state)}
          {...register('addressData.state', {
            validate: requiredWhenFilled('Estado é obrigatório'),
          })}
          error={!!errors.addressData?.state}
          helperText={errors.addressData?.state?.message}
        >
          <option value="">Selecione o Estado</option>
          {STATES.map((uf) => (
            <option key={uf.sigla} value={uf.sigla}>
              {uf.nome}
            </option>
          ))}
        </TextField>
        <TextField
          label="Município"
          inputProps={{ readOnly: locked.city }}
          sx={lockedSx(locked.city)}
          {...register('addressData.city', {
            validate: requiredWhenFilled('Município é obrigatório'),
          })}
          error={!!errors.addressData?.city}
          helperText={errors.addressData?.city?.message}
        />
        <TextField
          label="Bairro"
          inputProps={{ readOnly: locked.district }}
          sx={lockedSx(locked.district)}
          {...register('addressData.district', {
            validate: requiredWhenFilled('Bairro é obrigatório'),
          })}
          error={!!errors.addressData?.district}
          helperText={errors.addressData?.district?.message}
        />
        <TextField
          label="Endereço"
          sx={{ gridColumn: { sm: 'span 2' }, ...lockedSx(locked.address) }}
          inputProps={{ readOnly: locked.address }}
          {...register('addressData.address', {
            validate: requiredWhenFilled('Endereço é obrigatório'),
          })}
          error={!!errors.addressData?.address}
          helperText={errors.addressData?.address?.message}
        />
        <TextField
          label="Número"
          {...register('addressData.number', {
            validate: requiredWhenFilled('Número é obrigatório'),
          })}
          error={!!errors.addressData?.number}
          helperText={errors.addressData?.number?.message}
        />
        <TextField label="Complemento (opcional)" {...register('addressData.complement')} />
        <TextField
          label="Ponto de referência (opcional)"
          sx={{ gridColumn: { sm: 'span 2' } }}
          {...register('addressData.referencePoint')}
        />
      </Box>
    </Paper>
  );
};
